import { create } from 'zustand';
import type { HollowScriptRuntime } from '../../engine/scripting/hollowScriptRuntime';

/**
 * The current play session's HollowScript runtime, if one exists - a
 * module-level singleton, matching `useLiveSceneStore`'s own posture (one
 * live play session at a time). `PreviewRoute` constructs and registers it
 * (dynamically importing `../../engine/scripting`, so the `@hollow/script` +
 * Worker bundle is only fetched when a Preview session actually starts, not
 * on every app load); `DockConsole` (`editor-panels.tsx`) reads it to know
 * whether there's anything real to run a script against right now.
 *
 * Only a type is imported here from `hollowScriptRuntime.ts` - this module
 * itself pulls in no part of `@hollow/script`.
 */
interface ScriptConsoleState {
  readonly runtime: HollowScriptRuntime | null;
  setRuntime: (runtime: HollowScriptRuntime | null) => void;
  /** The current project's declared capability grant (task item 4,
   * GameSetupDoc.scriptCapabilities) - `undefined` means "not configured",
   * which `DockConsole` treats as "grant every v1 capability", matching
   * tier 1's own default posture. Held as plain strings (see
   * GameSetupDoc's own doc comment for why), narrowed by the reader. */
  readonly allowedCapabilities: readonly string[] | undefined;
  setAllowedCapabilities: (names: readonly string[] | undefined) => void;
  /**
   * Whether Preview's console overlay is open. Preview mode is deliberately
   * chrome-free (`showChrome = mode !== 'preview'` in screens-editor.tsx, a
   * full-screen "Play" experience like every other engine's) so `BottomDock`
   * itself never renders there - `PreviewRoute` renders a dedicated small
   * overlay instead, toggled by the same backtick shortcut
   * (`toggleConsole` in screens-editor.tsx) that already opens Edit mode's
   * own docked Console tab.
   */
  readonly open: boolean;
  toggleOpen: () => void;
  /**
   * Lines `print`ed by scripts the session ran on its own behalf - the
   * project's run-on-start scripts (`attachBootScripts`), which have no
   * console input line to attach output to. `DockConsole` folds these into
   * its scrollback as they arrive; kept here (not in DockConsole's own
   * local state) because the runtime that produces them is constructed by
   * `PreviewRoute`, outside any console component.
   */
  readonly sessionOutput: readonly { readonly id: number; readonly text: string }[];
  appendSessionOutput: (text: string) => void;
  clearSessionOutput: () => void;
}

let nextSessionOutputId = 1;

export const useScriptConsoleStore = create<ScriptConsoleState>((set) => ({
  runtime: null,
  setRuntime: (runtime) => set({ runtime }),
  allowedCapabilities: undefined,
  setAllowedCapabilities: (allowedCapabilities) => set({ allowedCapabilities }),
  open: false,
  toggleOpen: () => set((state) => ({ open: !state.open })),
  sessionOutput: [],
  appendSessionOutput: (text) => set((state) => ({ sessionOutput: [...state.sessionOutput, { id: nextSessionOutputId++, text }] })),
  clearSessionOutput: () => set({ sessionOutput: [] }),
}));
